import { AxiosResponse } from 'axios'
import { ErrorType, IJackpot, ITable, IPaginatedResponse, IResponse, JackpotData } from '../interfaces'
import api from './index'

export interface JackpotsRequest {
  page?: number
  perPage?: number
  status?: number[]
  halls?: number[]
  tables?: number[]
  tablesByHalls?: boolean
}

export interface HallTableJackpots {
  tables: Pick<ITable, 'id' | 'name' | 'login'>[]
  jackpots: IJackpot[]
}

const listParams = ({ page = 1, perPage = 20, status = [], halls = [], tables = [], tablesByHalls = false }: JackpotsRequest) => {
  const params: Record<string, number> = { page, 'per-page': perPage }
  Object.entries({ status, halls, tables }).forEach(([field, ids]) => {
    ids.forEach((id, index) => { params[`f[${field}][${index}]`] = id })
  })
  if (tablesByHalls) params['f[tables_by_halls]'] = 1
  return params
}

// The API also reports application errors inside successful HTTP responses.
const unwrapResponse = <T,>(result: { data?: unknown; error?: unknown }) => {
  if (result.error) return { error: result.error }
  const response = result.data as IResponse<T>
  if (response.code !== 200) return { error: { status: response.code, data: response.data } }
  return { data: response.data }
}

export const getJackpotErrors = (error: unknown): ErrorType[] => {
  if (!error) return []
  const value = error as { data?: unknown; message?: string }
  const payload = value.data ?? error
  const details = (payload as { data?: unknown })?.data ?? payload
  const errors = Array.isArray(details) ? details : [details]
  return errors.map(item => ({
    field: typeof item?.field === 'string' ? item.field : '',
    message: typeof item === 'string' ? item : item?.message || value.message || 'Ошибка запроса джекпота',
  }))
}

export const jackpotApi = api.injectEndpoints({
  endpoints: builder => ({
    getJackpotTables: builder.query<HallTableJackpots['tables'], void>({
      queryFn: async (_arg, _api, _options, baseQuery) => {
        const tables = new Map<number, HallTableJackpots['tables'][number]>()
        let pageCount = 1
        for (let page = 1; page <= pageCount; page++) {
          const result = await baseQuery({ url: 'gambling-table', method: 'get', params: { page, 'per-page': 50, expand: 'login' } })
          const response = unwrapResponse<ITable[]>(result)
          if (response.error) return { error: response.error }
          response.data!.forEach(({ id, name, login }) => tables.set(id, { id, name, login }))
          pageCount = Number((result.meta as AxiosResponse | undefined)?.headers?.['x-pagination-page-count']) || 1
        }
        return { data: Array.from(tables.values()) }
      },
    }),
    getHallTableJackpots: builder.query<HallTableJackpots, number>({
      queryFn: async (hallId, _api, _options, baseQuery) => {
        const loadPages = async <T,>(url: string, params: Record<string, string | number>) => {
          const items: T[] = []
          let pageCount = 1
          for (let page = 1; page <= pageCount; page++) {
            const result = await baseQuery({ url, method: 'get', params: { ...params, page, 'per-page': 50 } })
            const response = unwrapResponse<T[]>(result)
            if (response.error) return { error: response.error }
            items.push(...response.data!)
            pageCount = Number((result.meta as AxiosResponse | undefined)?.headers?.['x-pagination-page-count']) || 1
          }
          return { data: items }
        }
        const tableResult = await loadPages<ITable>('gambling-table', { 'f[hall_id]': hallId, expand: 'login' })
        if (tableResult.error) return { error: tableResult.error }
        const tables = Array.from(new Map(tableResult.data!.map(table => [table.id, { id: table.id, name: table.name, login: table.login }])).values())
        if (!tables.length) return { data: { tables, jackpots: [] } }
        const result = await loadPages<IJackpot>('jackpot', listParams({ status: [0, 1], halls: [hallId], tablesByHalls: true }))
        if (result.error) return { error: result.error }
        const tableIds = new Set(tables.map(table => table.id))
        const jackpots = new Map<number, IJackpot>()
        result.data!.forEach(jackpot => {
          if ([0, 1].includes(jackpot.status) && jackpot.gambling_tables.some(id => tableIds.has(id))) {
            jackpots.set(jackpot.id, jackpot)
          }
        })
        return { data: { tables, jackpots: Array.from(jackpots.values()) } }
      },
      providesTags: ['jackpots'],
    }),
    findJackpots: builder.query<IPaginatedResponse<IJackpot[]>, JackpotsRequest>({
      queryFn: async (request, _api, _options, baseQuery) => {
        const result = await baseQuery({ url: 'jackpot', method: 'get', params: listParams(request) })
        const response = unwrapResponse<IJackpot[]>(result)
        if (response.error) return { error: response.error }
        const headers = (result.meta as AxiosResponse | undefined)?.headers
        return { data: {
          items: response.data!,
          meta: {
            currentPage: Number(headers?.['x-pagination-current-page']) || request.page || 1,
            pageCount: Number(headers?.['x-pagination-page-count']) || 1,
            perPage: Number(headers?.['x-pagination-per-page']) || request.perPage || 20,
            totalCount: Number(headers?.['x-pagination-total-count']) || 0,
          },
        } }
      },
      providesTags: ['jackpots'],
    }),
    getAllJackpots: builder.query<IJackpot[], void>({
      queryFn: async (_arg, _api, _options, baseQuery) => {
        const items: IJackpot[] = []
        let pageCount = 1
        for (let page = 1; page <= pageCount; page++) {
          const result = await baseQuery({ url: 'jackpot', method: 'get', params: listParams({ page, perPage: 50 }) })
          const response = unwrapResponse<IJackpot[]>(result)
          if (response.error) return { error: response.error }
          items.push(...response.data!)
          pageCount = Number((result.meta as AxiosResponse | undefined)?.headers?.['x-pagination-page-count']) || 1
        }
        return { data: items }
      },
      providesTags: ['jackpots'],
    }),
    getJackpot: builder.query<IJackpot, number>({
      queryFn: async (id, _api, _options, baseQuery) =>
        unwrapResponse<IJackpot>(await baseQuery({ url: `jackpot/${id}`, method: 'get', params: { expand: 'halls,gambling_tables' } })),
      providesTags: (_result, _error, id) => [{ type: 'jackpots', id }],
    }),
    addJackpot: builder.mutation<IJackpot, JackpotData>({
      queryFn: async (data, _api, _options, baseQuery) =>
        unwrapResponse<IJackpot>(await baseQuery({ url: 'jackpot', method: 'post', data })),
      invalidatesTags: (_result, error) => error ? [] : ['jackpots'],
    }),
    changeJackpot: builder.mutation<IJackpot, { id: number; data: JackpotData }>({
      queryFn: async ({ id, data }, _api, _options, baseQuery) =>
        unwrapResponse<IJackpot>(await baseQuery({
          url: `jackpot/${id}`, method: 'put',
          data: data.gambling_tables.length > 0 ? { ...data, halls: [] } : data,
        })),
      invalidatesTags: (_result, error) => error ? [] : ['jackpots'],
    }),
    deleteJackpot: builder.mutation<unknown, number>({
      queryFn: async (id, _api, _options, baseQuery) =>
        unwrapResponse<unknown>(await baseQuery({ url: `jackpot/${id}`, method: 'delete' })),
      invalidatesTags: (_result, error) => error ? [] : ['jackpots'],
    }),
  }),
})

export const {
  useGetJackpotTablesQuery,
  useGetHallTableJackpotsQuery,
  useFindJackpotsQuery, useGetAllJackpotsQuery, useGetJackpotQuery,
  useAddJackpotMutation, useChangeJackpotMutation, useDeleteJackpotMutation,
} = jackpotApi
